import { useEffect, useState } from 'react';
import { EntityServiceAPI } from 'api/EntityServiceAPI';

export const BENEFICIAL_OWNERSHIP_LABEL = 'Beneficial Ownership';

// Keep a small compatibility set available while the live aggregation loads.
// Beneficial Ownership is backed by its own index and setting, so it must never
// be exposed as a watchlist lens even if a legacy aggregation includes it.
export const ENTITY_SCREENING_LENS_FALLBACK = [
  'EU Restricted Parties',
  'Forced Labor Entities',
  'Worldwide Restricted Parties',
  'US Restricted Parties',
  'Carriers',
  'Restricted Ownership and Affiliates',
];

let cachedEntityLenses: string[] | null = null;
let cachedEntityLensesAt = 0;
let entityLensesRequest: Promise<string[]> | null = null;
const ENTITY_LENS_CACHE_MS = 60_000;

export const withoutBeneficialOwnership = (lenses: string[]): string[] =>
  Array.from(
    new Set(
      lenses.filter((lens) => lens.trim() !== BENEFICIAL_OWNERSHIP_LABEL),
    ),
  );

// Keep the legacy marker in API payloads during the rolling migration so older
// servers/workers still honor the dedicated switch. It is never returned as a
// selectable lens by this hook.
export const withBeneficialOwnershipCompatibility = (
  lenses: string[],
  enabled: boolean,
): string[] => {
  const watchlistLenses = withoutBeneficialOwnership(lenses);
  return enabled
    ? [...watchlistLenses, BENEFICIAL_OWNERSHIP_LABEL]
    : watchlistLenses;
};

export const resolveBeneficialOwnershipEnabled = (
  screeningLists: unknown,
  dedicatedSetting: unknown,
  defaultWhenUnconfigured = true,
): boolean => {
  if (Array.isArray(screeningLists) && screeningLists.length > 0) {
    return screeningLists.includes(BENEFICIAL_OWNERSHIP_LABEL);
  }
  return typeof dedicatedSetting === 'boolean'
    ? dedicatedSetting
    : defaultWhenUnconfigured;
};

const loadEntityLenses = (): Promise<string[]> => {
  if (
    cachedEntityLenses &&
    Date.now() - cachedEntityLensesAt < ENTITY_LENS_CACHE_MS
  ) {
    return Promise.resolve(cachedEntityLenses);
  }

  if (!entityLensesRequest) {
    entityLensesRequest = EntityServiceAPI.getEntityScreeningLenses()
      .then((lenses) => {
        if (lenses.length) {
          cachedEntityLenses = withoutBeneficialOwnership(lenses);
          cachedEntityLensesAt = Date.now();
          return cachedEntityLenses;
        }
        // Keep the fallback usable for this render, but do not cache a failed
        // or empty response: a later mount should be allowed to retry.
        return ENTITY_SCREENING_LENS_FALLBACK;
      })
      .finally(() => {
        entityLensesRequest = null;
      });
  }

  return entityLensesRequest;
};

export const useEntityScreeningLenses = (
  enabled = true,
): { lenses: string[]; isLoading: boolean } => {
  const [lenses, setLenses] = useState<string[]>(
    () => cachedEntityLenses ?? ENTITY_SCREENING_LENS_FALLBACK,
  );
  const [isLoading, setIsLoading] = useState(
    () => enabled && cachedEntityLenses === null,
  );

  useEffect(() => {
    if (!enabled) return undefined;

    let active = true;
    setIsLoading(cachedEntityLenses === null);
    loadEntityLenses()
      .then((nextLenses) => {
        if (active) setLenses(nextLenses);
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [enabled]);

  return { lenses: lenses, isLoading: isLoading };
};
